// frontend/src/pages/company/CompanySettings.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { companyBankAccountAPI, companyAPI } from '../../services/api';
import CompanyLayout from '../../components/layout/CompanyLayout';
import getImageUrl from '../../utils/imageUrl';

const CompanySettings = () => {
  const { hasPermission, user } = useAuth();
  
  const [activeTab, setActiveTab] = useState('general');
  
  // ==========================================
  // GENERAL SETTINGS STATE
  // ==========================================
  const [generalSettings, setGeneralSettings] = useState({
    company_name: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalSaving, setGeneralSaving] = useState(false);

  // ==========================================
  // BANK ACCOUNTS STATE
  // ==========================================
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    iban_number: '',
    is_default: false
  });
  const [saving, setSaving] = useState(false);

  // ==========================================
  // NOTIFICATION SETTINGS STATE
  // ==========================================
  const [notificationSettings, setNotificationSettings] = useState({
    receive_delivery_notifications: true
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);

  // ==========================================
  // GENERAL SETTINGS FUNCTIONS
  // ==========================================
  const fetchGeneralSettings = async () => {
    setGeneralLoading(true);
    try {
      const companyId = user?.company_id;
      if (!companyId) return;
      
      const response = await companyAPI.getById(companyId);
      if (response.data.success) {
        const company = response.data.data;
        setGeneralSettings({
          company_name: company.company_name || '',
          logo: null
        });
        setLogoPreview(company.logo_url ? getImageUrl(company.logo_url) : null);
      }
    } catch (error) {
      console.error('Fetch general settings error:', error);
    } finally {
      setGeneralLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGeneralSettings(prev => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveGeneralSettings = async () => {
    setGeneralSaving(true);
    try {
      const companyId = user?.company_id;
      if (!companyId) return;
      
      const formDataObj = new FormData();
      formDataObj.append('company_name', generalSettings.company_name);
      if (generalSettings.logo) {
        formDataObj.append('logo', generalSettings.logo);
      }

      const response = await companyAPI.updateCompanySettings(companyId, formDataObj);
      if (response.data.success) {
        alert('Тохиргоо амжилттай хадгалагдлаа');
        window.location.reload();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setGeneralSaving(false);
    }
  };

  // ==========================================
  // BANK ACCOUNTS FUNCTIONS
  // ==========================================
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await companyBankAccountAPI.getAll();
      if (response.data.success) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Fetch accounts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAccount(null);
    setFormData({
      bank_name: '',
      account_number: '',
      account_name: '',
      iban_number: '',
      is_default: false
    });
    setShowModal(true);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      bank_name: account.bank_name,
      account_number: account.account_number,
      account_name: account.account_name,
      iban_number: account.iban_number || '',
      is_default: account.is_default
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.bank_name || !formData.account_number || !formData.account_name) {
      alert('Банкны нэр, дансны дугаар, эзэмшигчийн нэр оруулна уу');
      return;
    }

    setSaving(true);
    try {
      if (editingAccount) {
        await companyBankAccountAPI.update(editingAccount.id, formData);
        alert('Данс амжилттай шинэчлэгдлээ');
      } else {
        await companyBankAccountAPI.create(formData);
        alert('Данс амжилттай нэмэгдлээ');
      }
      setShowModal(false);
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Энэ дансыг устгахдаа итгэлтэй байна уу?')) return;
    
    try {
      await companyBankAccountAPI.delete(id);
      alert('Данс устгагдлаа');
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await companyBankAccountAPI.setDefault(id);
      fetchAccounts();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  // ==========================================
  // NOTIFICATION SETTINGS FUNCTIONS
  // ==========================================
  const fetchNotificationSettings = async () => {
    setNotificationLoading(true);
    try {
      const companyId = user?.company_id;
      if (!companyId) return;
      
      const response = await companyAPI.getNotificationSettings(companyId);
      if (response.data.success) {
        setNotificationSettings(response.data.data);
      }
    } catch (error) {
      console.error('Fetch notification settings error:', error);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setNotificationSaving(true);
    try {
      const companyId = user?.company_id;
      if (!companyId) return;
      
      const response = await companyAPI.updateNotificationSettings(companyId, notificationSettings);
      if (response.data.success) {
        alert('Тохиргоо хадгалагдлаа');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setNotificationSaving(false);
    }
  };

  // ==========================================
  // USE EFFECT
  // ==========================================
  useEffect(() => {
    if (activeTab === 'general') {
      fetchGeneralSettings();
    } else if (activeTab === 'bank-accounts') {
      fetchAccounts();
    } else if (activeTab === 'notifications') {
      fetchNotificationSettings();
    }
  }, [activeTab]);

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* ============================================ */}
        {/* TAB NAVIGATION - 3 TABS */}
        {/* ============================================ */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'general'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>⚙️</span>
                <span>Ерөнхий тохиргоо</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('bank-accounts')}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'bank-accounts'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>🏦</span>
                <span>Дансны тохиргоо</span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'notifications'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <span>🔔</span>
                <span>Мэдэгдэлийн тохиргоо</span>
              </span>
            </button>
          </div>
        </div>

        {/* ============================================ */}
        {/* GENERAL SETTINGS TAB */}
        {/* ============================================ */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span>🏢</span>
                  Компанийн мэдээлэл
                </h3>
                <p className="text-sm text-gray-500 mt-1">Sidebar дээр харагдах нэр, лого</p>
              </div>
              
              {generalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* Logo */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">Компаний лого</label>
                    <div className="flex items-center gap-6">
                      <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300 overflow-hidden">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                        ) : (
                          <span className="text-4xl text-gray-400">🏢</span>
                        )}
                      </div>
                      <div>
                        <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition font-medium">
                          <span>📷</span>
                          <span>Лого солих</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="hidden"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">PNG, JPG (150x150 px санал болгоно)</p>
                      </div>
                    </div>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Компаний нэр</label>
                    <input
                      type="text"
                      value={generalSettings.company_name}
                      onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_name: e.target.value }))}
                      className="w-full max-w-md px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="Компаний нэр"
                    />
                    <p className="text-xs text-gray-500 mt-1">Sidebar дээр харагдана</p>
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">ℹ️</span>
                      <div>
                        <p className="font-semibold text-blue-800">Анхааруулга</p>
                        <p className="text-sm text-blue-700">Компаний нэр, лого нь зөвхөн админ панелд харагдах дүрслэлийг өөрчилнө. Бусад мэдээллийг системийн админтай холбогдож өөрчлүүлнэ үү.</p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  {hasPermission('settings.edit') && (
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={handleSaveGeneralSettings}
                        disabled={generalSaving}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                      >
                        {generalSaving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Хадгалж байна...</span>
                          </>
                        ) : (
                          <>
                            <span>💾</span>
                            <span>Хадгалах</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* BANK ACCOUNTS TAB */}
        {/* ============================================ */}
        {activeTab === 'bank-accounts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Компанийн данснууд</h2>
                <p className="text-sm text-gray-500">Шопуудад COD төлбөр шилжүүлэх данснууд</p>
              </div>
              {hasPermission('settings.edit') && (
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <span>➕</span>
                  <span>Данс нэмэх</span>
                </button>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : accounts.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-4xl">🏦</span>
                  <p className="mt-2 text-gray-500">Данс бүртгэгдээгүй байна</p>
                  {hasPermission('settings.edit') && (
                    <button
                      onClick={handleCreate}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      Данс нэмэх
                    </button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {accounts.map((account) => (
                    <div key={account.id} className="p-4 hover:bg-gray-50 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">🏦</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{account.bank_name}</p>
                              {account.is_default && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  Үндсэн
                                </span>
                              )}
                              {!account.is_active && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                  Идэвхгүй
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 font-mono">{account.account_number}</p>
                            <p className="text-sm text-gray-500">{account.account_name}</p>
                          </div>
                        </div>
                        {hasPermission('settings.edit') && (
                          <div className="flex items-center gap-2">
                            {!account.is_default && (
                              <button
                                onClick={() => handleSetDefault(account.id)}
                                className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="Үндсэн болгох"
                              >
                                ⭐
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(account)}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
                            >
                              ✏️ Засах
                            </button>
                            <button
                              onClick={() => handleDelete(account.id)}
                              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              🗑️ Устгах
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* NOTIFICATIONS TAB */}
        {/* ============================================ */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span>🔔</span>
                  Мэдэгдэлийн тохиргоо
                </h3>
                <p className="text-sm text-gray-500 mt-1">Ямар мэдэгдлүүд хүлээн авахаа тохируулах</p>
              </div>
              
              {notificationLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {/* Delivery Notifications Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">📦</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Хүргэлтийн мэдэгдэл</p>
                        <p className="text-sm text-gray-500">Онлайн шоп хүргэлт үүсгэхэд мэдэгдэл хүлээн авах</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.receive_delivery_notifications}
                        onChange={(e) => setNotificationSettings({
                          ...notificationSettings,
                          receive_delivery_notifications: e.target.checked
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">💡</span>
                      <div>
                        <p className="font-semibold text-yellow-800">Системийн мэдэгдлүүд</p>
                        <p className="text-sm text-yellow-700">COD төлбөр, нэхэмжлэх зэрэг чухал мэдэгдлүүд үргэлж ирнэ. Зөвхөн хүргэлтийн мэдэгдлийг унтраах боломжтой.</p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  {hasPermission('settings.edit') && (
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={handleSaveNotificationSettings}
                        disabled={notificationSaving}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                      >
                        {notificationSaving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Хадгалж байна...</span>
                          </>
                        ) : (
                          <>
                            <span>💾</span>
                            <span>Хадгалах</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* BANK ACCOUNT MODAL */}
        {/* ============================================ */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">
                  {editingAccount ? 'Данс засах' : 'Шинэ данс нэмэх'}
                </h2>
              </div>
              <form onSubmit={handleSave} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Банкны нэр *</label>
                  <select
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Сонгоно уу</option>
                    <option value="Хаан банк">Хаан банк</option>
                    <option value="Голомт банк">Голомт банк</option>
                    <option value="Худалдаа хөгжлийн банк">Худалдаа хөгжлийн банк</option>
                    <option value="Хас банк">Хас банк</option>
                    <option value="Төрийн банк">Төрийн банк</option>
                    <option value="Капитрон банк">Капитрон банк</option>
                    <option value="Богд банк">Богд банк</option>
                    <option value="М банк">М банк</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дансны дугаар *</label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="1234567890"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Данс эзэмшигч *</label>
                  <input
                    type="text"
                    value={formData.account_name}
                    onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="Овог Нэр"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN (заавал биш)</label>
                  <input
                    type="text"
                    value={formData.iban_number}
                    onChange={(e) => setFormData({...formData, iban_number: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="MN..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_default" className="text-sm text-gray-700">Үндсэн данс болгох</label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Болих
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Хадгалж байна...' : 'Хадгалах'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

export default CompanySettings;